export type UsageMonthlyRollupItem = {
  product?: string;
  module?: string;
  costTotalUsd?: number;
  costApigwUsd?: number;
  costLambdaUsd?: number;
  costBedrockUsd?: number;
  costThirdPartyUsd?: number;
  costS3Usd?: number;
  costDynamodbUsd?: number;
  costStepFunctionsUsd?: number;
};

function n(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

function keyOfProduct(p: unknown): string {
  return String(p ?? '').trim().toLowerCase();
}

function keyOfModule(m: unknown): string {
  const raw = String(m ?? '').trim();
  const beforeHash = raw.split('#')[0] || '';
  const root = beforeHash.split('/')[0] || '';
  return root.trim().toLowerCase();
}

export type UsageServiceKey =
  | 'apigw'
  | 'lambda'
  | 'bedrock'
  | 'third_party'
  | 's3'
  | 'dynamodb'
  | 'step_functions';

const SERVICE_COST_FIELDS: Array<{
  key: UsageServiceKey;
  costField: keyof UsageMonthlyRollupItem;
}> = [
  { key: 'apigw', costField: 'costApigwUsd' },
  { key: 'lambda', costField: 'costLambdaUsd' },
  { key: 'bedrock', costField: 'costBedrockUsd' },
  { key: 'third_party', costField: 'costThirdPartyUsd' },
  { key: 's3', costField: 'costS3Usd' },
  { key: 'dynamodb', costField: 'costDynamodbUsd' },
  { key: 'step_functions', costField: 'costStepFunctionsUsd' },
];

/**
 * Builds a lookup of normalized product key -> total cost USD.
 * Normalization: lower-cased trimmed product name.
 */
export function buildProductCostUsdMap(items: UsageMonthlyRollupItem[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const it of items || []) {
    const key = keyOfProduct(it?.product);
    if (!key) continue;
    out[key] = (out[key] || 0) + n(it?.costTotalUsd);
  }
  return out;
}

/**
 * Builds a lookup of normalized product key -> service key -> cost USD.
 * Omits 0-value service costs.
 */
export function buildProductServiceCostUsdMap(
  items: UsageMonthlyRollupItem[],
): Record<string, Partial<Record<UsageServiceKey, number>>> {
  const out: Record<string, Partial<Record<UsageServiceKey, number>>> = {};
  for (const it of items || []) {
    const key = keyOfProduct(it?.product);
    if (!key) continue;

    for (const def of SERVICE_COST_FIELDS) {
      const add = n((it as any)?.[def.costField]);
      if (!add) continue;
      const bySvc = out[key] || (out[key] = {});
      bySvc[def.key] = n(bySvc[def.key]) + add;
    }
  }
  return out;
}

/**
 * Builds a lookup of normalized product key -> normalized module key -> total cost USD.
 * Normalization: lower-cased trimmed product/module.
 */
export function buildProductModuleCostUsdMap(
  items: UsageMonthlyRollupItem[],
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const it of items || []) {
    const p = keyOfProduct(it?.product);
    const m = keyOfModule((it as any)?.module);
    if (!p || !m) continue;
    const byModule = out[p] || (out[p] = {});
    byModule[m] = n(byModule[m]) + n(it?.costTotalUsd);
  }
  return out;
}

const MODULE_LABELS: Record<string, string> = {
  'ai2-gen-back': 'AI Generation Service',
  'jzl-aie-description-service': 'Description Service',
  'jzl-data-enhancement-hub': 'Data Enhancement Hub',
  'inventory-search': 'Inventory Search',
  'jams-api': 'JAMS',
  qr: 'QR Generator',
};

export function formatModuleLabel(moduleKey: unknown): string {
  const raw = String(moduleKey ?? '').trim();
  const k = raw.toLowerCase();
  return MODULE_LABELS[k] || raw;
}

export function formatUsd(v: unknown): string {
  const x = Number(v);
  const n = Number.isFinite(x) ? x : 0;
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Higher-precision currency formatting for breakdown views/tooltips.
 * Keeps the table cells at 2 decimals via `formatUsd`, but avoids
 * rounding tiny service costs down to $0.00 in the per-service breakdown.
 */
export function formatUsdPrecise(v: unknown): string {
  const x = Number(v);
  const n = Number.isFinite(x) ? x : 0;
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

